import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { X, RotateCcw, Wand2 } from "lucide-react";

interface PairBuilderProps {
  selectedNumbers: number[];
  value: [number, number][];
  onChange: (pairs: [number, number][]) => void;
  /** Texto de orientação da loteria (faixa recomendada de dezenas). */
  hint?: string;
}

/**
 * Permite ao usuário escolher manualmente como agrupar as dezenas
 * selecionadas em duplas. Toda dezena precisa aparecer em exatamente
 * uma dupla para a geração ser válida.
 */
export function PairBuilder({ selectedNumbers, value, onChange, hint }: PairBuilderProps) {
  const [pending, setPending] = useState<number | null>(null);

  // Limpa duplas inválidas quando a seleção muda
  useEffect(() => {
    const set = new Set(selectedNumbers);
    const cleaned = value.filter(([a, b]) => set.has(a) && set.has(b));
    if (cleaned.length !== value.length) onChange(cleaned);
    if (pending != null && !set.has(pending)) setPending(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNumbers]);

  const usedSet = useMemo(() => {
    const s = new Set<number>();
    value.forEach(([a, b]) => {
      s.add(a);
      s.add(b);
    });
    return s;
  }, [value]);

  const available = selectedNumbers.filter((n) => !usedSet.has(n));

  const handleClick = (n: number) => {
    if (usedSet.has(n)) return;
    if (pending == null) {
      setPending(n);
      return;
    }
    if (pending === n) {
      setPending(null);
      return;
    }
    onChange([...value, [pending, n]]);
    setPending(null);
  };

  const removePair = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const autoAscending = () => {
    const sorted = [...selectedNumbers].sort((a, b) => a - b);
    const pairs: [number, number][] = [];
    for (let i = 0; i < sorted.length - 1; i += 2) pairs.push([sorted[i], sorted[i + 1]]);
    onChange(pairs);
    setPending(null);
  };

  const autoFill = () => {
    const remaining = [...available];
    const pairs: [number, number][] = [...value];
    while (remaining.length >= 2) {
      const a = remaining.shift()!;
      const b = remaining.shift()!;
      pairs.push([a, b]);
    }
    onChange(pairs);
    setPending(null);
  };

  const clearAll = () => {
    onChange([]);
    setPending(null);
  };

  const oddCount = selectedNumbers.length % 2 !== 0;
  // Em seleção ímpar, sobra exatamente 1 dezena: a âncora (entra em todos os jogos).
  const anchor = oddCount && available.length === 1 ? available[0] : null;
  const incomplete = oddCount ? available.length > 1 : available.length > 0;
  const missing = oddCount ? available.length - 1 : available.length;

  return (
    <div className="space-y-3 rounded-lg border border-lt-teal/40 bg-lt-teal/5 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[12px] font-semibold text-lt-teal">
          Monte as duplas que devem caminhar juntas
        </div>
        <div className="flex flex-wrap gap-1">
          <Button type="button" size="sm" variant="outline" onClick={autoAscending}>
            <RotateCcw className="mr-1 h-3 w-3" /> Ordem crescente
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={autoFill} disabled={available.length < 2}>
            <Wand2 className="mr-1 h-3 w-3" /> Auto-completar
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={clearAll} disabled={value.length === 0}>
            Limpar
          </Button>
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground">
        Clique em uma dezena disponível para iniciar uma dupla e clique em outra para fechá-la.
        As dezenas já em duplas ficam destacadas embaixo.
        {oddCount
          ? " Com quantidade ímpar de dezenas, a dezena que sobrar vira a âncora e entra em todos os jogos."
          : ""}
      </p>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}

      <div>
        <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
          Dezenas disponíveis ({available.length})
        </div>
        <div className="flex flex-wrap gap-1.5">
          {selectedNumbers.length === 0 && (
            <span className="text-[12px] text-muted-foreground">Marque dezenas no quadro acima.</span>
          )}
          {selectedNumbers.map((n) => {
            const used = usedSet.has(n);
            const isPending = pending === n;
            return (
              <button
                key={n}
                type="button"
                onClick={() => handleClick(n)}
                disabled={used}
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full border text-[12px] font-medium transition-all",
                  used && "cursor-not-allowed border-dashed border-muted-foreground/30 bg-transparent text-muted-foreground/40 line-through",
                  !used && !isPending && "border-lt-teal/50 bg-background text-foreground hover:bg-lt-teal/10",
                  isPending && "border-lt-teal bg-lt-teal text-background shadow ring-2 ring-lt-teal/40",
                )}
              >
                {String(n).padStart(2, "0")}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
          Duplas formadas ({value.length}
          {selectedNumbers.length > 0 ? ` / ${Math.floor(selectedNumbers.length / 2)}` : ""})
        </div>
        {value.length === 0 ? (
          <p className="text-[12px] text-muted-foreground">Nenhuma dupla formada ainda.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {value.map(([a, b], idx) => (
              <span
                key={`${a}-${b}-${idx}`}
                className="inline-flex items-center gap-1 rounded-full border border-lt-teal bg-lt-teal/15 px-2 py-1 text-[11px] font-medium text-foreground"
              >
                <span className="font-mono tabular-nums">{String(a).padStart(2, "0")}</span>
                <span className="text-muted-foreground">+</span>
                <span className="font-mono tabular-nums">{String(b).padStart(2, "0")}</span>
                <button
                  type="button"
                  onClick={() => removePair(idx)}
                  className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  aria-label="Desfazer dupla"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {anchor != null && (
        <div className="flex items-center gap-2 rounded-md border border-primary/50 bg-primary/10 px-2 py-1.5">
          <span className="text-[11px] font-semibold text-primary">Dezena âncora (entra em todos os jogos):</span>
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-primary bg-primary font-mono text-[12px] text-primary-foreground">
            {String(anchor).padStart(2, "0")}
          </span>
        </div>
      )}
      {incomplete && (
        <p className="text-[11px] text-primary">
          Faltam {missing} dezena(s) para formar todas as duplas.
        </p>
      )}
    </div>
  );
}